import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import { Box, IconButton, Tooltip } from "@mui/material";
import { grey } from "@mui/material/colors";
import Field from "components/Field";
import FieldRichText from "components/FieldRichText";
import RegisterPlugins from "components/TinyMCEPlugins/Plugins/RegisterPlugins";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  ConnectDragPreview,
  ConnectDragSource,
  ConnectDropTarget,
  DragSource,
  DragSourceConnector,
  DragSourceMonitor,
  DropTarget,
  DropTargetConnector,
  DropTargetMonitor,
} from "react-dnd";
import { FieldArrayRenderProps } from "react-final-form-arrays";
import { useIntl } from "react-intl";
import { IEvaluationQuestionAnswer } from "services/KnowfullyAdminClient";
import { Editor } from "tinymce";
import { Mode, QuestionTypeEnum } from "types/common-types";
import { DragItem, DraggableInstance, shouldMove } from "utils/dndUtils";

type DraggableAnswerProps = {
  fieldName: string;
  index: number;
  totalCount: number;
  isGhostAnswer: boolean;
  mode: Mode;
  answer: IEvaluationQuestionAnswer;
  onMoveAnswer: (fromIndex: number, toIndex: number) => void;
  onRemoveAnswer: (index: number) => void;
  onSetAsAnswer: (index: number) => void;

  // Supplied by react-dnd HOCs
  isDragging: boolean;
  connectDragSource: ConnectDragSource;
  connectDropTarget: ConnectDropTarget;
  connectDragPreview: ConnectDragPreview;
  questionType: string;
  disabled?: boolean;
};

const DraggableAnswer = forwardRef<HTMLDivElement, DraggableAnswerProps>(
  (
    {
      fieldName,
      index,
      answer,
      totalCount,
      onRemoveAnswer,
      onSetAsAnswer,
      isGhostAnswer,
      mode,
      isDragging,
      connectDragSource,
      connectDragPreview,
      connectDropTarget,
      questionType,
      disabled,
    },
    ref
  ) => {
    const { formatMessage: t } = useIntl();
    const elementRef = useRef(null);

    connectDragSource(elementRef);
    connectDropTarget(elementRef);
    connectDragPreview(connectDropTarget(elementRef));

    useImperativeHandle<any, DraggableInstance>(ref, () => ({
      getNode: () => elementRef.current,
    }));

    const opacity = isDragging ? 0 : 1;

    function validateHTML(ed: Editor) {
      const str = ed.getContent();
      const hasHtmlTags = str.includes("&lt;") && str.includes("&gt;");
      if (hasHtmlTags) {
        ed.notificationManager.open({
          text: t({ id: "htmlWarningMessage" }),
          type: "warning",
        });
      }
    }

    useEffect(() => {
      if (
        mode === Mode.Create &&
        !isGhostAnswer &&
        (answer.value === undefined || answer.value === "&nbsp;")
      ) {
        onRemoveAnswer(index);
      }
    }, [
      answer.value,
      mode,
      isGhostAnswer,
      index,
      answer.isAnswer,
      onRemoveAnswer,
    ]);

    return (
      <Box mb={2}>
        <div ref={elementRef} style={{ opacity }}>
          <Box
            p={1}
            display="flex"
            alignItems="center"
            sx={{ backgroundColor: grey[100] }}
          >
            {questionType !== QuestionTypeEnum.TextQuestion &&
              totalCount > 1 &&
              answer.value &&
              connectDragSource(
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    cursor: disabled ? "default" : "move",
                    marginRight: 8,
                  }}
                >
                  <DragHandleIcon />
                </div>
              )}
            <Field name={`${fieldName}.value`}>
              {({ input, meta }) => {
                return (
                  <FieldRichText
                    input={input}
                    meta={meta}
                    init={{
                      placeholder: t({ id: "insertAnswerPlaceholder" }),
                      statusbar: true,
                      height: 200,
                      inline: true,
                      setup: (ed) => {
                        RegisterPlugins({ editor: ed });
                        ed.on("keyup", (e) => {
                          validateHTML(ed);
                        });
                      },
                    }}
                    disabled={disabled}
                  />
                );
              }}
            </Field>

            {questionType !== QuestionTypeEnum.TextQuestion && answer.value && (
              <Tooltip title={"Mark as answer"}>
                <IconButton
                  color={answer.isAnswer ? "primary" : "default"}
                  onClick={() => onSetAsAnswer(index)}
                  data-cy={`answer${index}IsAnswerButton`}
                >
                  {answer.isAnswer ? (
                    <CheckCircleIcon
                      titleAccess={t({ id: "questionFormAnswerTitle" })}
                    />
                  ) : (
                    <CheckCircleOutlineIcon />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </div>
      </Box>
    );
  }
);

const EnabledDraggableAnswer = DropTarget(
  "ANSWER",
  {
    hover(
      { index, totalCount, mode, onMoveAnswer, disabled }: DraggableAnswerProps,
      monitor: DropTargetMonitor,
      component: DraggableInstance
    ) {
      if (!component || disabled) {
        return null;
      }

      // node = HTML Div element from imperative API
      const node = component.getNode();
      if (!node) {
        return null;
      }

      const item = monitor.getItem<DragItem>();
      const dragIndex = item.index;
      const hoverIndex = index;

      // Prevent moving items after the ghost answer
      const maxHoverIndex = totalCount - (mode === Mode.Create ? 1 : 0);

      if (hoverIndex >= maxHoverIndex) {
        return;
      }

      if (shouldMove(hoverIndex, node, dragIndex, monitor)) {
        onMoveAnswer(item.index, index);

        // Note: we're mutating the monitor item here!
        // Generally it's better to avoid mutations,
        // but it's good here for the sake of performance
        // to avoid expensive index searches.
        item.index = hoverIndex;
      }
    },
  },
  (connect: DropTargetConnector) => ({
    connectDropTarget: connect.dropTarget(),
  })
)(
  DragSource(
    "ANSWER",
    {
      beginDrag: ({ answer, index }: DraggableAnswerProps) => ({
        id: answer.id,
        index,
      }),
    },
    (connect: DragSourceConnector, monitor: DragSourceMonitor) => ({
      connectDragSource: connect.dragSource(),
      connectDragPreview: connect.dragPreview(),
      isDragging: monitor.isDragging(),
    })
  )(DraggableAnswer)
);

type OrderableAnswersProps = {
  fields: FieldArrayRenderProps<
    IEvaluationQuestionAnswer,
    HTMLElement
  >["fields"];
  mode: Mode;
  questionType: string;
  addAnswer?: () => string;
  disabled?: boolean;
};

const OrderableAnswers = ({
  fields,
  mode,
  addAnswer,
  ...rest
}: OrderableAnswersProps) => {
  const [ghostAnswerId, setGhostAnswerId] = useState<string | null>(null);

  const onSetAsAnswer = useCallback(
    (index: number) => {
      for (let i = 0; i < (fields.length ?? 0); i++) {
        const value = fields.value[i];
        if (i === index && !value.isAnswer) {
          fields.update(i, { ...value, isAnswer: true });
        } else if (i !== index && value.isAnswer) {
          fields.update(i, { ...value, isAnswer: false });
        } else if (i === index && value.isAnswer) {
          fields.update(i, { ...value, isAnswer: false });
        }
      }
    },
    [fields]
  );

  const onSetAsMultipleAnswer = (index: number) => {
    const value = fields.value[index];
    const currentAnswers = fields.value.filter((x) => x.isAnswer);
    if (currentAnswers.length === 1 && value.isAnswer) {
      return;
    }
    fields.update(index, { ...value, isAnswer: !value.isAnswer });
  };

  useEffect(() => {
    if (addAnswer && !fields.value.find((x) => !x.value)) {
      const ghostAnswerId = addAnswer();
      setGhostAnswerId(ghostAnswerId);
    }
  }, [addAnswer, fields.value]);

  useEffect(() => {
    if (fields.length && !fields.value.find((x) => x.isAnswer)) {
      onSetAsAnswer(0);
    }
  }, [fields.length, fields.value, onSetAsAnswer]);

  return (
    <div>
      {fields.map((name, i) => {
        const answer = fields.value[i];
        const isGhostAnswer = answer.id === ghostAnswerId;
        return (
          <EnabledDraggableAnswer
            key={answer.id}
            answer={answer}
            totalCount={fields.length!}
            fieldName={name}
            index={i}
            onMoveAnswer={fields.move}
            onRemoveAnswer={fields.remove}
            onSetAsAnswer={
              QuestionTypeEnum.MultipleAnswers.toString() === rest.questionType
                ? onSetAsMultipleAnswer
                : onSetAsAnswer
            }
            isGhostAnswer={isGhostAnswer}
            mode={mode}
            {...rest}
          />
        );
      })}
    </div>
  );
};

export default OrderableAnswers;
